
import React, { useState, useEffect, useContext, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import i18next from 'i18next';
import './App.css';
import { PButton, PButtonPure, PorscheDesignSystemProvider } from '@porsche-design-system/components-react';

import SuccessScreen from 'MainApp/Pages/StripePayment/Success';
import NotFound from 'MainApp/Pages/404/NotFound';
import TourSelection from 'MainApp/Pages/TourSelection/TourSelection';
import CancelScreen from 'MainApp/Pages/StripePayment/Cancel';
import TourInformation from 'MainApp/Pages/TourInformation/TourInformation';
import useIsPhone from 'MainApp/assets/utils/hooks/useIsPhone';
import TourContainer from 'Components/TourContainer/TourContainer';
import TourDaySelection from 'MainApp/Pages/TourDaySelection/TourDaySelection';
import CalendarDebug from 'MainApp/Pages/CalendarDebug/CalendarDebug';
import { LanguageProvider } from 'MainApp/assets/utils/context/LanguageContext';
import { ToursProvider } from 'MainApp/assets/utils/context/TourContext';
import Son from 'pages/Son';
import { AuthContext } from 'MainApp/assets/utils/context/AuthContext';
import { getSupportedLanguageCode } from 'MainApp/assets/utils/js/localeUtils';
import TourListIframe from 'Components/TourListIframe';
import styled from 'styled-components';
import { borderRadiusLarge, frostedGlassStyle, themeDarkPrimary, themeLightStateHover } from '@porsche-design-system/components-js/styles';
import MockModeBadge from './mock/MockModeBadge';
// import Dashboard from 'pages/Dashboard';

const pageVariants = {
    out_FadeOut: {
        opacity: 0,
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    initial: {
        opacity: 0,
        x: '100vw'
    },
    in: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    out: {
        opacity: 0,
        x: '-100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },

    slideInFromRight: {
        opacity: 0,
        x: '100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    slideOutToLeft: {
        opacity: 0,
        x: '-100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    slideOutToRight: {
        opacity: 0,
        x: '100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },

    generalOut: {
        opacity: 0,
        transition: {
            duration: 0.6,
            // delay: 0.3
        }
    },
    generalInitial: {
        opacity: 1
    },
};

const pageTransition = {
    type: 'tween',
    duration: 0.5
};

const MotionRoute = ({ element, initialVariants, exitVariants }) => (
    <motion.div
        initial={initialVariants ? initialVariants : "initial"}
        animate="in"
        exit={exitVariants ? exitVariants : "out"}
        variants={pageVariants}
        transition={pageTransition}
        className="motionContainerStyle"

    >
        {element}
    </motion.div>
);

const App = () => {

    const { setLocalTourUUID, setToken, setOriginUrl, setEventId } = useContext(AuthContext);
    // console.log("Test1");

    useEffect(() => {
        window.parent.postMessage({ action: "iframeReady" }, "*");

        // console.log("Test2");
        const handleMessage = async (event) => {

            const { action, payload, origin, id, tourUUID } = event.data; // Obtener acción y payload
            if (action === "updateToken") {
                // console.log("Received token payload:", payload);

                setToken(payload);
                setOriginUrl(origin);
                setEventId(id);
                const language = event.data?.language;
                if (language) {
                    i18next.changeLanguage(getSupportedLanguageCode(language));
                }
                if (tourUUID) {
                    setLocalTourUUID(tourUUID);
                }
            }
        };

        // Escuchar eventos de mensaje
        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [setEventId, setLocalTourUUID, setOriginUrl, setToken]);

    const location = useLocation();
    const [prevLocation, setPrevLocation] = useState(location);
    const [direction, setDirection] = useState('forward');
    const isPhone = useIsPhone();

    useEffect(() => {
        if (location.pathname.includes('/tour/')) {
            if (prevLocation.pathname.includes('/tour/') && location.pathname !== prevLocation.pathname) {
                setDirection('forward');
            } else if (location.pathname === '/') {
                setDirection('backward');
            } else {
                setDirection('forward');
            }
        } else if (prevLocation.pathname.includes('/tour/') && location.pathname !== '/') {
            setDirection('forward');
        }
        setPrevLocation(location);
    }, [location, prevLocation]);

    const getVariants = () => {
        if (location.pathname.includes('/tour/')) {
            if (direction === 'forward') {
                return { initial: 'slideInFromRight', exit: 'slideOutToLeft' };
            } else if (direction === 'backward') {
                return { initial: 'slideInFromRight', exit: 'slideOutToRight' };
            }
        }
        return { initial: 'initial', exit: 'out' };
    };

    const getCurrentScreen = () => {
        if (location.pathname === '/') {
            return 'TourSelection';
        } else if (location.pathname.includes('/tour/')) {
            return 'TourInformation';
        }
        return 'Other';
    };

    const isDevEnvironment = process.env.NODE_ENV !== 'production';


    const FrostedGlass = styled.div({
        ...frostedGlassStyle,
        // color: themeDarkPrimary,
        // background: "red",
        // background: themeLightStateHover,
        // borderRadius: borderRadiusLarge,
        width: "100%",
        height: "100%",
        // padding: spacingFluidMedium,
    });

    return (
        // <FrostedGlass>
        <TourContainer screen={getCurrentScreen()}>
            <MockModeBadge />
            {/* <PButton>TEST</PButton>
<PButton variant="primary" style={{ '--pds-theme-light-primary': '#28A745' }}>
  Botón con fondo verde
</PButton>
<PButtonPure style={{ backgroundColor: '#28A745', color: '#fff', border: '2px solid #28A745' }}>
  Botón verde
</PButtonPure> */}
            <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                    <Route exact
                        path="/"
                        element={<MotionRoute
                            element={<TourSelection />}
                            initialVariants="generalOut"
                            exitVariants="generalOut"
                        />}
                    />
                    <Route exact
                        path="/tour/details/:slug"
                        element={<MotionRoute
                            element={<TourInformation isPhone={isPhone} />}
                            initialVariants="generalOut"
                            exitVariants="generalOut"
                        />}
                    />
                    <Route exact
                        path="/reservation/:slug"
                        element={<MotionRoute
                            element={<TourDaySelection />}
                            initialVariants="generalOut"
                            exitVariants="generalOut"
                        />}
                    />
                    <Route exact
                        path="/reservation/cancel"
                        element={<MotionRoute
                            element={<CancelScreen />}
                            initialVariants={getVariants().initial}
                            exitVariants={getVariants().exit}
                        />}
                    />
                    <Route exact
                        path="/reservation/success"
                        element={<MotionRoute
                            element={<SuccessScreen />}
                            initialVariants={getVariants().initial}
                            exitVariants={getVariants().exit}
                        />}
                    />
                    <Route exact path="/son" element={<Son />} />
                    {isDevEnvironment && (
                        <Route exact path="/debug/calendar" element={<CalendarDebug />} />
                    )}
                    <Route path="*" element={<NotFound />} />
                    {/* <Route path="/dashboard" element={<Dashboard />} /> */}
                    <Route exact
                        path="/iframeList"
                        element={<MotionRoute
                            element={<TourListIframe />}
                            initialVariants="generalOut"
                            exitVariants="generalOut"
                        />}
                    />
                </Routes>
            </AnimatePresence>
        </TourContainer >
        // </FrostedGlass>

    );
};
const FORCE_COLOR = '#0056B3';

const AppWrapper = () => {
  /**
   * 1. Esperamos al "complete" del DOM para asegurarnos de que
   *    las hojas de estilo del Porsche Design System ya están cargadas.
   * 2. Generamos dinámicamente una regla CSS con TODOS los tokens
   *    que empiecen por --p- y los forzamos con !important.
   ***/
 useLayoutEffect(() => {
      const pTokens = new Set();

      // 1) Recorre todas las hojas de estilo que el navegador haya cargado
      for (const sheet of Array.from(document.styleSheets)) {
        let rules;
        try {
          rules = (sheet).cssRules;
        } catch (e) {
          // No podemos leer hojas de estilo de otro dominio → se ignoran
          continue;
        }

        // 2) Por cada regla de estilo, mira qué propiedades define
        for (const rule of Array.from(rules)) {
          if (rule instanceof CSSStyleRule) {
            for (const prop of Array.from(rule.style)) {
              if (prop.startsWith('--p-')) {
                pTokens.add(prop);
              }
            }
          }
        }
      }

      // 3) Si no hemos encontrado ninguno, puede que Porsche inyecte tokens
      //    en un <style> dinámico: opcionalmente, comprueba también
      //    document.adoptedStyleSheets… (para Shadow DOM con constructable stylesheets).

      // 4) Genera e inyecta UNA etiqueta <style> con todos los tokens forzados
      if (pTokens.size > 0) {
        const styleEl = document.createElement('style');
        styleEl.id = 'porsche-overrides';
        styleEl.textContent = `
          :root {
            ${[...pTokens].map((t) => `${t}: ${FORCE_COLOR} !important;`).join('\n')}
          }
        `;
        document.head.appendChild(styleEl);
      } else {
        console.warn('[Porsche Override] No se encontraron tokens `--p-…` en document.styleSheets');
      }
   }, []);
    return (
    <Router>
      <PorscheDesignSystemProvider>
        <LanguageProvider>
          <ToursProvider>
            <App />
          </ToursProvider>
        </LanguageProvider>
      </PorscheDesignSystemProvider>
    </Router>
  );
};

export default AppWrapper;
